import {
  ACCOUNT_LABEL_TABLE_NAME,
  ACCOUNT_LEASE_TOPCLASS,
  AccountLabel,
  Contact,
  CreateAccountLabelInput,
  CreateAccountLabelMutationVariables,
  CustomSubscription,
  CustomSubscriptionType,
  DEFAULT_GRAPHQL_OBJECT_DEPTH_FOR_SYNC_QUERY,
  OnCreateAccountLabelSubscription,
  OnCreateCustomSubscriptionSubscription,
  OnDeleteAccountLabelSubscription,
  removeNestedFieldsExcept,
  OnDeleteCustomSubscriptionSubscription,
  OnUpdateAccountLabelSubscription,
  OnUpdateCustomSubscriptionSubscription,
  UpdateAccountLabelInput,
  cleanInputCreate,
  cleanInputUpdate,
  createAccountLabel as createAccountLabelMutation,
  deleteAccountLabel as deleteAccountLabelMutation,
  getAccountLabel as getAccountLabelQuery,
  getAccountLabelsFor,
  getFilteredAccountLabelsForContactOrDefault,
  getOrCreateAccountLabelForContractor,
  getReadId,
  getTableClientId,
  getTypedCustomSubscriptionValueForRefetch,
  onCreateAccountLabel,
  onCreateCustomSubscription,
  onDeleteAccountLabel,
  onDeleteCustomSubscription,
  onUpdateAccountLabel,
  onUpdateCustomSubscription,
  syncAccountLabels,
  updateAccountLabel as updateAccountLabelMutation,
} from '@rentguru/commons-utils';
import { deleteAndHideEntityWithFetchBefore, list, mutation, useSubscriptions } from '@up2rent/graphql';
import { isNil } from 'lodash';
import { Reducer, createContext, useContext, useEffect, useMemo, useReducer } from 'react';

import { useLeases } from './LeasesContext';
import { useUser } from './UserContext';

export interface AccountLabelContextType {
  accountLabels: AccountLabel[];
  accountLabelsFetched: boolean;
  accountLabelsLoading: boolean;
  accountLabelsError: string | undefined;
  getAccountLabel: (id: string) => AccountLabel | undefined;
  getAccountLabelsFromIds: (accountLabelIds: string[] | null | undefined) => AccountLabel[];
  createAccountLabel: (input: Omit<CreateAccountLabelInput, 'clientId' | 'readId'>) => Promise<AccountLabel>;
  updateAccountLabel: (updates: UpdateAccountLabelInput) => Promise<AccountLabel>;
  deleteAccountLabel: (accountLabel: AccountLabel) => Promise<AccountLabel>;
  getAccountLabelsForLease: (leaseId: string) => AccountLabel[];
  getAccountLabelsForInvoiceSenderId: (invoiceSenderId: string) => AccountLabel[];
  getAccountLabelForContactOrInsert: (
    contractor: Contact,
    ownerId?: string | null
  ) => Promise<AccountLabel | undefined>;
  getLeaseTopClassAccountLabelForInvoiceSenderId: (invoiceSenderId: string) => AccountLabel;
}

type AccountLabelAction =
  | { type: 'SHOULD_FETCH' }
  | { type: 'IS_FETCHING' }
  | { type: 'FETCHED'; payload: { accountLabels: AccountLabel[] } }
  | { type: 'ERROR'; payload: { error: string } }
  | { type: 'ADD_ACCOUNT_LABEL' | 'UPDATE_ACCOUNT_LABEL'; payload: { accountLabel: AccountLabel } }
  | { type: 'DELETE_ACCOUNT_LABEL'; payload: { id: string } };

interface AccountLabelState {
  accountLabels: AccountLabel[] | null;
  loading: boolean;
  shouldFetch: boolean;
  error: string | undefined;
}

const initialState: AccountLabelState = {
  accountLabels: null,
  loading: false,
  shouldFetch: false,
  error: undefined,
};

const accountLabelReducer = (state: AccountLabelState, action: AccountLabelAction): AccountLabelState => {
  switch (action.type) {
    case 'SHOULD_FETCH':
      if (state.loading || state.shouldFetch) return state;
      return { ...state, shouldFetch: true };
    case 'IS_FETCHING':
      return { ...state, loading: true };
    case 'FETCHED':
      return {
        ...state,
        loading: false,
        shouldFetch: false,
        error: undefined,
        accountLabels: action.payload.accountLabels,
      };
    case 'ERROR':
      return { ...state, loading: false, shouldFetch: false, error: action.payload.error };
    case 'ADD_ACCOUNT_LABEL':
      return {
        ...state,
        accountLabels: [
          ...(state.accountLabels ?? []).filter((a) => a.id !== action.payload.accountLabel.id),
          action.payload.accountLabel,
        ],
      };
    case 'UPDATE_ACCOUNT_LABEL':
      return {
        ...state,
        accountLabels: (state.accountLabels ?? []).map((a) =>
          a.id === action.payload.accountLabel.id ? action.payload.accountLabel : a
        ),
      };
    case 'DELETE_ACCOUNT_LABEL':
      return {
        ...state,
        accountLabels: (state.accountLabels ?? []).filter((a) => a.id !== action.payload.id),
      };
    default:
      return state;
  }
};

const AccountLabelContext = createContext<AccountLabelContextType | null>(null);

const ACCOUNT_LABEL_RELATIONS_TO_REMOVE = ['bankAccount', 'contact', 'owner'];
const ACCOUNT_LABEL_NESTED_FIELDS_TO_KEEP = ['labels', 'customValues'];

const ACCOUNT_LABELS_QUERY = removeNestedFieldsExcept(
  syncAccountLabels,
  DEFAULT_GRAPHQL_OBJECT_DEPTH_FOR_SYNC_QUERY,
  ACCOUNT_LABEL_RELATIONS_TO_REMOVE,
  ACCOUNT_LABEL_NESTED_FIELDS_TO_KEEP
);

export const AccountLabelsContextProvider: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer<Reducer<AccountLabelState, AccountLabelAction>>(
    accountLabelReducer,
    initialState
  );
  const { clientId, isOwner, userId, clientContactId, isFetchingUser } = useUser();
  const { getLease } = useLeases();

  useSubscriptions<
    OnCreateAccountLabelSubscription,
    OnUpdateAccountLabelSubscription,
    OnDeleteAccountLabelSubscription
  >(
    onCreateAccountLabel,
    onUpdateAccountLabel,
    onDeleteAccountLabel,
    (data) =>
      dispatch({ type: 'ADD_ACCOUNT_LABEL', payload: { accountLabel: data.onCreateAccountLabel as AccountLabel } }),
    (data) =>
      dispatch({ type: 'UPDATE_ACCOUNT_LABEL', payload: { accountLabel: data.onUpdateAccountLabel as AccountLabel } }),
    (data) =>
      dispatch({ type: 'DELETE_ACCOUNT_LABEL', payload: { id: (data.onDeleteAccountLabel as AccountLabel).id } })
  );

  useSubscriptions<
    OnCreateCustomSubscriptionSubscription,
    OnUpdateCustomSubscriptionSubscription,
    OnDeleteCustomSubscriptionSubscription
  >(
    onCreateCustomSubscription,
    onUpdateCustomSubscription,
    onDeleteCustomSubscription,
    (data) => {
      const newData = data.onCreateCustomSubscription as CustomSubscription;
      if (
        newData.name === CustomSubscriptionType.REFETCH &&
        getTypedCustomSubscriptionValueForRefetch(newData).table === ACCOUNT_LABEL_TABLE_NAME
      ) {
        dispatch({ type: 'SHOULD_FETCH' });
      }
    },
    () => {},
    () => {}
  );

  useEffect(() => {
    if (!isFetchingUser && clientId) {
      dispatch({ type: 'SHOULD_FETCH' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, isFetchingUser]);

  useEffect(() => {
    const fetchAndSet = async () => {
      dispatch({ type: 'IS_FETCHING' });
      try {
        const accountLabels = await list<AccountLabel>(
          ACCOUNT_LABELS_QUERY,
          'clientId',
          getTableClientId(clientId!, ACCOUNT_LABEL_TABLE_NAME)
        );
        dispatch({ type: 'FETCHED', payload: { accountLabels } });
      } catch (e) {
        dispatch({ type: 'ERROR', payload: { error: String(e) } });
      }
    };
    if (state.shouldFetch && !isFetchingUser && clientId) fetchAndSet();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.shouldFetch, isFetchingUser, clientId]);

  const getAccountLabel = (id: string): AccountLabel | undefined =>
    (state.accountLabels ?? []).find((a) => a.id === id);

  const getAccountLabelsFromIds = (accountLabelIds: string[] | null | undefined): AccountLabel[] =>
    (accountLabelIds ?? []).reduce<AccountLabel[]>((accumulator, accountLabelId) => {
      const accountLabel = getAccountLabel(accountLabelId);
      if (!isNil(accountLabel)) {
        accumulator.push(accountLabel);
      }
      return accumulator;
    }, []);

  const createAccountLabel = async (
    input: Omit<CreateAccountLabelInput, 'clientId' | 'readId'>
  ): Promise<AccountLabel> => {
    const ownerAlreadyHasPerOwnerLabels =
      !isNil(input.ownerId) &&
      (state.accountLabels ?? []).some((accountLabel) => accountLabel.ownerId === input.ownerId);

    const accountLabel = await mutation<AccountLabel, CreateAccountLabelMutationVariables>(createAccountLabelMutation, {
      input: {
        ...(cleanInputCreate(input) as CreateAccountLabelInput),
        ownerId: ownerAlreadyHasPerOwnerLabels ? input.ownerId : undefined,
        readId: getReadId(clientId!, 'AccountLabel'),
        clientId: getTableClientId(clientId!, 'AccountLabel'),
        ...(isOwner && { writers: [userId!] }),
      },
    });
    dispatch({ type: 'ADD_ACCOUNT_LABEL', payload: { accountLabel } });
    return accountLabel;
  };

  const updateAccountLabel = async (updates: UpdateAccountLabelInput): Promise<AccountLabel> => {
    const result = await mutation<AccountLabel>(updateAccountLabelMutation, {
      input: cleanInputUpdate(updates, false),
    });
    dispatch({ type: 'UPDATE_ACCOUNT_LABEL', payload: { accountLabel: result } });
    return result;
  };

  const deleteAccountLabel = async (accountLabel: AccountLabel): Promise<AccountLabel> => {
    const result = await deleteAndHideEntityWithFetchBefore<AccountLabel>(
      accountLabel,
      getAccountLabelQuery,
      deleteAccountLabelMutation
    );
    dispatch({ type: 'DELETE_ACCOUNT_LABEL', payload: { id: accountLabel.id } });
    return result;
  };

  const getAccountLabelsForInvoiceSenderId = (invoiceSenderId: string): AccountLabel[] =>
    getFilteredAccountLabelsForContactOrDefault(state.accountLabels ?? [], invoiceSenderId, clientContactId);

  const getAccountLabelsForLease = (leaseId: string): AccountLabel[] => {
    const lease = getLease(leaseId);
    if (!lease) return state.accountLabels ?? [];
    return getAccountLabelsForInvoiceSenderId(lease.invoiceSenderId);
  };

  const getLeaseTopClassAccountLabelForInvoiceSenderId = (invoiceSenderId: string): AccountLabel => {
    const accountLabels = getAccountLabelsForInvoiceSenderId(invoiceSenderId);
    return accountLabels.find((a) => a.topClass === ACCOUNT_LEASE_TOPCLASS)!;
  };

  const getAccountLabelForContactOrInsert = async (
    contact: Contact,
    ownerId?: string | null
  ): Promise<AccountLabel | undefined> => {
    const filteredAccountLabels = getAccountLabelsFor(state.accountLabels ?? [], ownerId);
    return getOrCreateAccountLabelForContractor(contact.id, clientId ?? '', filteredAccountLabels, createAccountLabel);
  };

  const values = useMemo(
    () => ({
      accountLabels: state.accountLabels ?? [],
      accountLabelsFetched: state.accountLabels !== null,
      accountLabelsLoading: state.loading,
      accountLabelsError: state.error,
      getAccountLabel,
      getAccountLabelsFromIds,
      createAccountLabel,
      updateAccountLabel,
      deleteAccountLabel,
      getAccountLabelsForLease,
      getAccountLabelsForInvoiceSenderId,
      getAccountLabelForContactOrInsert,
      getLeaseTopClassAccountLabelForInvoiceSenderId,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state, isFetchingUser, getLease, clientId, clientContactId]
  );

  return <AccountLabelContext.Provider value={values}>{children}</AccountLabelContext.Provider>;
};

export const useAccountLabels = (): AccountLabelContextType => {
  const context = useContext<AccountLabelContextType | null>(AccountLabelContext);
  if (!context) {
    throw new Error('`useAccountLabels` hook must be used within an `AccountLabelsContextProvider` component');
  }
  return context;
};
